import React from 'react'
import { useParams } from 'react-router-dom'
import SimplePaymentService from '../service/SimplePaymentService'
import SimplePayment, { SimplePaymentRetrieve } from '../domain/SimplePayment'
import { statusLabel } from '../domain/Status'
import useEntity from '../hooks/useEntity'
import { paths } from '../routes'
import FindBox from './ui/FindBox'
import EntityState from './ui/EntityState'
import EntitySummary from './ui/EntitySummary'
import StatusChanger from './ui/StatusChanger'

const SimplePaymentForm: React.FC = () => {
    const { id } = useParams()
    const {
        entity: payment,
        setEntity,
        loading,
        error,
        find,
        refresh,
    } = useEntity<SimplePaymentRetrieve>({
        kind: 'simplePayment',
        id,
        retrieve: (paymentId) => SimplePaymentService.retrieve(paymentId),
        pathFor: paths.simplePayment,
        notFound: 'Simple payment not found. Check the ID and try again.',
        remember: (found) => ({ label: found.wallexRef }),
    })

    const changeStatus = (status: string, reason?: string) =>
        SimplePaymentService.update(payment?.id as string, { status, failureReason: reason }).then(() =>
            setEntity((current) => current && { ...current, status, failureReason: reason ?? current.failureReason })
        )

    return (
        <div className="stack">
            <FindBox kind="simplePayment" title="Find a Simple Payment" placeholder="Enter Simple Payment ID" value={id} loading={loading} onFind={find} />
            <EntityState id={id} loading={loading} error={error} ready={!!payment} emptyText="Enter a Simple Payment ID above, or pick a recent one.">
                {payment && (
                    <>
                        <EntitySummary
                            title="Simple Payment Information"
                            status={payment.status}
                            statusLabel={statusLabel(payment.status, SimplePayment.statuses(payment))}
                            items={[
                                { label: 'Payment ID', copy: payment.id },
                                { label: 'Wallex reference', copy: payment.wallexRef },
                                { label: 'Channel', value: payment.paymentChannel },
                                { label: 'Failure reason', value: payment.failureReason },
                            ]}
                            refreshing={loading}
                            onRefresh={refresh}
                        />
                        <StatusChanger
                            noun="payment"
                            current={payment.status}
                            statuses={SimplePayment.statuses(payment)}
                            journey={SimplePayment.journey}
                            hints={SimplePayment.hints}
                            onSubmit={changeStatus}
                        />
                    </>
                )}
            </EntityState>
        </div>
    )
}

export default SimplePaymentForm
